import { Link } from '@inertiajs/react';
import classNames from 'classnames';
import { ReactNode, useState } from 'react';
import { AlertTriangle, ArrowRight, Check, ChevronDown, ChevronUp, Copy, ExternalLink, Eye, EyeOff, Info } from 'react-feather';
import { Trans, useTranslation } from 'react-i18next';

import { useMutation } from '../../../Shared/api';
import { assetUrl } from '../../../Shared/helpers';
import { useLocale } from '../../../Shared/locale';
import { useNotifier } from '../../../Shared/Notifier/NotificationProvider';
import { AButton, Button, LinkButton } from '../../../Shared/UI/Button';
import { Card } from '../../../Shared/UI/Card';
import { ConfirmButton } from '../../../Shared/UI/ConfirmButton';
import { FadeIn } from '../../../Shared/UI/FadeIn';
import { Icon } from '../../../Shared/UI/Icon';
import { Input } from '../../../Shared/UI/Input';
import { useCopy } from '../../../Shared/useCopy';
import { useRouter } from '../../router';
import { DonationForm, SharedProps } from '../../types';
import Container from '../../UI/Container';
import Layout from '../../UI/Layout';
import { DonationFormLayout } from './DonationFormLayout';

export interface DonationFormPageProps extends SharedProps {
  donationForm: DonationForm & {
    subscriptions_count: number;
    oneoff_donations_count: number;
    revenue: number;
    deletable: boolean;
  };
}

export default function DonationFormPage({
  donationForm,
  charity,
}: DonationFormPageProps) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const notifier = useNotifier();
  const { formatCurrency, formatNumber } = useLocale();

  const [copyDonationFormUrl, copiedDonationFormUrl] = useCopy(donationForm.url);
  const [showRecurringInfo, setShowRecurringInfo] = useState(false);

  const [toggleDonationForm, { loading }] = useMutation(routes.toggle_donation_form(donationForm.id), {
    preserveScroll: true,
    onSuccess: () => {
      notifier.notify(
        !donationForm.enabled ? t('backend:donation_form_enabled') : t('backend:donation_form_disabled'),
        !donationForm.enabled ? Eye : EyeOff
      );
    },
  });

  const [deleteDonationForm, { loading: deleting }] = useMutation(routes.delete_donation_form(donationForm.id), {
    onSuccess: () => {
      notifier.notify(t('backend:donation_form_deleted'));
    },
    onError: () => {
      notifier.notify(t('backend:something_went_wrong'), AlertTriangle);
    },
  });

  return (
    <Container>
      <div className="grid grid-cols-12 gap-8">
        <div className="col-span-3 space-y-4 text-slate-600" style={{ minWidth: 0 }}>
          <div>
            <h4 className="font-medium text-slate-500">
              {t('backend:oneoff_donations')}
            </h4>
            {formatNumber(donationForm.oneoff_donations_count)}
            {donationForm.oneoff_donations_count > 0 && (
              <span className="text-slate-400">
                {' · '}
                <Link href={`${routes.donations_page()}?donation_form_id=${donationForm.id}&recurring=0`}>
                  {t('backend:view')}
                </Link>
              </span>
            )}
          </div>

          <div>
            <h4 className="font-medium text-slate-500">
              {t('backend:recurring_donations')}
            </h4>
            {formatNumber(donationForm.subscriptions_count)}
            {donationForm.subscriptions_count > 0 && (
              <span className="text-slate-400">
                {' · '}
                <Link href={`${routes.subscriptions_page()}?donation_form_id=${donationForm.id}`}>
                  {t('backend:view')}
                </Link>
              </span>
            )}
          </div>

          <div>
            <h4 className="font-medium text-slate-500">
              {t('backend:revenue')}
            </h4>
            {formatCurrency(donationForm.revenue)}
          </div>

          {donationForm.deletable && (
            <div>
              <ConfirmButton
                onClick={() => deleteDonationForm()}
                loading={deleting}
                variant="danger"
                size="sm"
              >
                {t('backend:delete_donation_form')}
              </ConfirmButton>
            </div>
          )}
        </div>

        <div className="col-span-9 space-y-6">
          <Card panels header>
            <div>
              <h3>
                {t('backend:actions')}
              </h3>
            </div>
            <Step icon="project-icon-settings.svg">
              <div>
                <h4>
                  {t('backend:configure_donation_form')}
                  {' '}
                  <span className="font-normal text-slate-400 text-sm ml-1">
                    {t('backend:optional_parentheses')}
                  </span>
                </h4>
              </div>
              <div className="mb-2">
                {t('backend:configure_donation_form_description')}
              </div>
              <LinkButton href={routes.donation_form_settings_page(donationForm.id)} variant="secondary">
                {t('backend:settings')}
                <Icon className="ml-1">
                  <ArrowRight />
                </Icon>
              </LinkButton>
            </Step>
            <Step icon="project-icon-mails.svg">
              <div>
                <h4>
                  {t('backend:set_donation_form_mails')}
                  {' '}
                  <span className="font-normal text-slate-400 text-sm ml-1">
                    {t('backend:optional_parentheses')}
                  </span>
                </h4>
              </div>
              <div className="mb-2">
                {t('backend:set_donation_form_mails_descripton')}
              </div>
              <LinkButton href={routes.donation_form_mails_page(donationForm.id)} variant="secondary">
                {t('backend:mails')}
                <Icon className="ml-1">
                  <ArrowRight />
                </Icon>
              </LinkButton>
            </Step>
            {donationForm.enabled && (
              <Step icon="project-icon-fundraisers.svg">
                <div>
                  <h4>
                    {t('backend:recruit_donors')}
                  </h4>
                </div>
                <div className="mb-2">
                  {t('backend:recruit_donors_description')}
                </div>
                <div className="flex space-x-2">
                  <Input
                    type="text"
                    value={donationForm.url}
                    readOnly
                    onFocus={(event) => event.target.select()}
                  />
                  <Button
                    onClick={() => copyDonationFormUrl()}
                    variant="secondary"
                    className={classNames(copiedDonationFormUrl && '!text-slate-400')}
                    title={t('backend:copy_link')}
                  >
                    <Icon className="mr-1">
                      {copiedDonationFormUrl ? <Check /> : <Copy />}
                    </Icon>
                    {t('backend:copy')}
                  </Button>
                  <AButton
                    href={donationForm.url}
                    target="_blank" rel="noopener noreferrer"
                    variant="secondary"
                    data-supporta-embed="false"
                    title={t('backend:open_link')}
                  >
                    <Icon className="mr-1">
                      <ExternalLink />
                    </Icon>
                    {t('backend:open')}
                  </AButton>
                </div>
              </Step>
            )}
            {!donationForm.enabled && (
              <Step icon="project-icon-launch.svg">
                <strong>
                  {t('backend:publish_donation_form')}
                </strong>
                <div className="mb-2">
                  {t('backend:publish_donation_form_description')}
                </div>
                <Button
                  onClick={() => toggleDonationForm({ enabled: true })}
                  variant="secondary"
                  disabled={loading}
                >
                  {!donationForm.enabled ? t('backend:enable_donation_form') : t('backend:donation_form_enabled')}
                </Button>
              </Step>
            )}
          </Card>

          <Card panels>
            <div className="!py-6">
              <a
                onClick={() => setShowRecurringInfo(!showRecurringInfo)}
                role="button"
                className="text-blue-600 font-medium hover:text-blue-500"
              >
                <Icon className="mr-1">
                  <Info />
                </Icon>
                {t('backend:how_recurring_donations_work')}
                <Icon className="ml-1">
                  {showRecurringInfo ? <ChevronUp /> : <ChevronDown />}
                </Icon>
              </a>
              <FadeIn
                when={showRecurringInfo}
                render={() => (
                  <ul className="list-decimal mt-2 pl-4">
                    <li>
                      <Trans i18nKey="backend:how_recurring_donations_work_1">
                        <Link href={routes.donation_form_settings_page(donationForm.id)} />
                      </Trans>
                    </li>
                    <li>
                      {t('backend:how_recurring_donations_work_2')}
                    </li>
                    <li>
                      {t('backend:how_recurring_donations_work_3', { charity: charity.title })}
                    </li>
                    <li>
                      {t('backend:how_recurring_donations_work_4')}
                    </li>
                  </ul>

                )}
              />
            </div>
          </Card>
        </div>
      </div>
    </Container>
  );
}

const Step = ({
  icon,
  children,
}: {
  icon: string;
  children?: ReactNode;
}) => {
  return (
    <div className="grid grid-cols-[96px_1fr] gap-6 sm:gap-8">
      <div>
        <img src={assetUrl(`images/${icon}`)} className="block w-24 h-24" />
      </div>
      <div>
        {children}
      </div>
    </div>
  );
};

DonationFormPage.layout = (page: ReactNode) => <Layout><DonationFormLayout>{page}</DonationFormLayout></Layout>;
